export const isSelectOrRadioField = (type: string): boolean => {
  return type === 'select' || type === 'multiselect' || type === 'radio';
};

export const parseDateValue = (value: string | number | Date): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDateTimeValue = (
  value: string | number | Date,
  options: Intl.DateTimeFormatOptions,
): string | null => {
  const parsed = parseDateValue(value);
  if (!parsed) {
    return null;
  }
  try {
    return new Intl.DateTimeFormat(undefined, options).format(parsed);
  } catch {
    return parsed.toLocaleString();
  }
};

export const formatSubmittedTimestamp = (value?: string | number | Date): string | null => {
  if (!value) {
    return null;
  }
  return formatDateTimeValue(value, { dateStyle: 'medium', timeStyle: 'short' });
};

export const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const CONSTANTS = {
  MESSAGES: {
    NOT_SPECIFIED: 'Not specified',
  },
};

// Helper to format select/radio field values
const formatSelectOrRadioValue = (
  options: Array<{ id?: string; value?: string; label?: string }>,
  value: unknown,
): string => {
  if (Array.isArray(value)) {
    const labels = value
      .map(val => {
        const option = options.find(opt => opt.id === val || opt.value === val);
        return option ? option.label : String(val);
      })
      .filter(label => label !== undefined && label !== null && label !== '');
    
    return labels.length === 0 ? CONSTANTS.MESSAGES.NOT_SPECIFIED : labels.join(', ');
  }
  
  const option = options.find(opt => opt.id === value || opt.value === value);
  return option ? option.label : String(value);
};

// Helper to format array values
const formatArrayValue = (value: unknown[]): string => {
  const sanitized = value
    .filter(item => item !== undefined && item !== null && item !== '')
    .map(item => String(item));
  
  return sanitized.length === 0 ? CONSTANTS.MESSAGES.NOT_SPECIFIED : sanitized.join(', ');
};

// Helper to format date/datetime values
const formatDateValue = (fieldType: string, value: unknown): string | null => {
  if (fieldType !== 'date' && fieldType !== 'datetime') {
    return null;
  }
  
  if (typeof value !== 'string' && typeof value !== 'number' && !(value instanceof Date)) {
    return null;
  }

  const options = fieldType === 'date' 
    ? { dateStyle: 'medium' as const } 
    : { dateStyle: 'medium' as const, timeStyle: 'short' as const };
  
  return formatDateTimeValue(value, options);
};

// Helper to format record (object) values
const formatRecordValue = (value: Record<string, unknown>): string => {
  if (typeof value['label'] === 'string') {
    return String(value['label']);
  }
  
  if (typeof value['name'] === 'string') {
    return String(value['name']);
  }
  
  if (typeof value['value'] === 'string') {
    return String(value['value']);
  }
  
  return JSON.stringify(value);
};

export const formatFieldValue = (
  field: { type?: string; options?: Array<{ id?: string; value?: string; label?: string }> },
  value: unknown,
): string => {
  // Handle empty/null/undefined values
  if (value === undefined || value === null || value === '') {
    return CONSTANTS.MESSAGES.NOT_SPECIFIED;
  }

  // Handle fields without type
  if (!field.type) {
    return String(value);
  }

  // Handle select/radio fields
  if (isSelectOrRadioField(field.type) && field.options && field.options.length > 0) {
    return formatSelectOrRadioValue(field.options, value);
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return formatArrayValue(value);
  }

  // Handle booleans
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  // Handle date/datetime fields
  const formattedDate = formatDateValue(field.type, value);
  if (formattedDate) {
    return formattedDate;
  }

  // Handle record (object) values
  if (isRecord(value)) {
    return formatRecordValue(value);
  }

  // Default: convert to string
  return String(value);
};

